import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

/**
 * CI link controller for ticket detail sidebar.
 * Allows searching and linking Configuration Items to tickets.
 *
 * data-controller="support--ci-link"
 * data-support--ci-link-search-url-value="/support/configuration_items/search.json"
 * data-support--ci-link-ticket-id-value="uuid"
 * data-support--ci-link-link-url-value="/support/tickets/uuid/ci_links"
 * data-support--ci-link-no-results-value="No configuration items found"
 */
export default class extends Controller {
  static targets = [
    "modal",
    "searchInput",
    "results",
    "relationshipSelect",
    "form",
    "ciIdField",
  ]

  static values = {
    searchUrl: String,
    ticketId: String,
    linkUrl: String,
    noResults: String,
  }

  connect() {
    this._searchTimeout = null
  }

  openModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      requestAnimationFrame(() => {
        if (this.hasSearchInputTarget) this.searchInputTarget.focus()
      })
    }
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      this._resetState()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  _resetState() {
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden")
      this.resultsTarget.replaceChildren()
    }
  }

  search() {
    const query = this.searchInputTarget.value.trim()
    if (this._searchTimeout) clearTimeout(this._searchTimeout)

    if (query.length < 2) {
      this.resultsTarget.classList.add("hidden")
      this.resultsTarget.replaceChildren()
      return
    }

    this._searchTimeout = setTimeout(() => this._performSearch(query), 300)
  }

  async _performSearch(query) {
    try {
      const url = `${this.searchUrlValue}?q=${encodeURIComponent(query)}`
      const response = await get(url, { responseKind: "json" })
      if (!response.ok) return

      const data = await response.json
      const items = data.configuration_items || []
      this._renderResults(items)
    } catch (e) {
      console.warn("CI search failed:", e)
    }
  }

  _renderResults(items) {
    this.resultsTarget.replaceChildren()

    if (items.length === 0) {
      const p = document.createElement("p")
      p.className = "px-3 py-2 text-sm text-muted-foreground"
      p.textContent = this.noResultsValue
      this.resultsTarget.appendChild(p)
      this.resultsTarget.classList.remove("hidden")
      return
    }

    items.forEach((ci) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.action = "click->support--ci-link#selectCi"
      btn.dataset.ciId = ci.id
      btn.dataset.ciName = ci.name
      btn.dataset.ciType = ci.ciType || ci.ci_type
      btn.className =
        "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors cursor-pointer text-left"

      const typeSpan = document.createElement("span")
      typeSpan.className =
        "text-xs font-medium text-muted-foreground shrink-0 uppercase"
      typeSpan.textContent = (ci.ciType || ci.ci_type || "").replace(/_/g, " ")

      const nameSpan = document.createElement("span")
      nameSpan.className = "truncate text-foreground"
      nameSpan.textContent = ci.name

      btn.appendChild(typeSpan)
      btn.appendChild(nameSpan)
      this.resultsTarget.appendChild(btn)
    })

    this.resultsTarget.classList.remove("hidden")
  }

  selectCi(event) {
    const btn = event.currentTarget
    const id = btn.dataset.ciId

    if (this.hasCiIdFieldTarget) {
      this.ciIdFieldTarget.value = id
    }

    this.resultsTarget.classList.add("hidden")
    this.searchInputTarget.value = btn.dataset.ciName
  }

  submit() {
    if (!this.hasCiIdFieldTarget || !this.ciIdFieldTarget.value) return
    this.formTarget.requestSubmit()
  }
}
